import { uuid } from '@elfsight/lib-utils';
import {
  emailSettings,
  integrationsSettings,
  longTextSettings,
  phoneSettings,
  respondentNotificationSettings,
  selfNotificationSettings,
  shortTextSettings,
  submitActionsSettings
} from '@elfsight/widget-form-builder/widget';
import {
  carouselAppearanceDefaultSettings as libCarouselAppearanceDefaultSettings,
  priceDefaultSettings as libPriceDefaultSettings,
  widgetButtonDefaults as libWidgetButtonDefaults,
  widgetTitleDefaultSettings
} from '@elfsight/widget-ui';
import type {
  FormFunctionality,
  OldPriceValue,
  PricingTableSettings
} from '../types';

export const widgetButtonDefaults = {
  ...libWidgetButtonDefaults,
  fullWidth: true,
  mode: 'outline',
  captionColor: 'rgba(17, 17, 17, 0.7)'
} as const;
export const priceDefaultSettings = {
  ...libPriceDefaultSettings,
  customPrice: 'Contact us for pricing'
};
const {
  currency: _currency,
  period: _period,
  pricingModel: _pricingModel,
  unit: _unit,
  ...rest
} = priceDefaultSettings;

export const oldPriceDefaultSettings: OldPriceValue = {
  ...rest,
  enabled: false
};
export const discountDefaultSettings = {
  customLabel: 'Special Deal',
  enabled: false
} as const;

const { carouselPaginationColor: _, ...carouselAppearanceDefaultSettings } =
  libCarouselAppearanceDefaultSettings;

export const defaultFormSettings: FormFunctionality = {
  fields: [
    {
      ...shortTextSettings,
      id: uuid(),
      type: 'shortText',
      label: 'Name',
      fieldID: 'name'
    },
    {
      ...emailSettings,
      id: uuid(),
      type: 'email'
    },
    {
      ...phoneSettings,
      id: uuid(),
      type: 'phone',
      label: 'Phone Number',
      fieldID: 'phone_number'
    },
    {
      ...longTextSettings,
      id: uuid(),
      type: 'longText',
      label: 'Message',
      fieldID: 'message'
    }
  ],
  submitButtonText: 'Submit',
  submitRedirectURL: '',
  recaptcha: false,
  integrationSettingsID: '',
  integrationStripe: {
    id: '',
    publishableKey: '',
    cancellationPolicyText: ''
  },
  checkoutSuccessTitle: 'Payment Successful',
  checkoutSuccessText:
    'Your payment has been processed successfully. Thank you!',
  ...submitActionsSettings,
  ...selfNotificationSettings,
  ...respondentNotificationSettings,
  ...integrationsSettings
};

export const defaultSettings: PricingTableSettings = {
  multipleTablesMode: false,
  language: {
    language: 'en',
    label: 'English (United States)',
    messages: {}
  },
  tables: [
    {
      id: 'ktygs1mf',
      name: 'Table 1',
      caption: '',
      columns: [],
      visible: true
    }
  ],
  formHeading: 'Contact Us',
  formCaption: '',

  layout: 'grid',
  width: {
    auto: true,
    customValue: 900
  },

  ...defaultFormSettings,

  ...widgetTitleDefaultSettings,

  columnStyle: 'style1',
  primaryColor: 'rgb(25, 123, 255)',
  secondaryColor: 'rgb(255, 255, 255)',
  font: {
    value: undefined
  },

  toggleColor: 'rgb(25, 123, 255)',

  columnCornerRadius: 16,

  ...carouselAppearanceDefaultSettings,

  headTextColor: 'rgb(17, 17, 17)',
  headBackgroundColor: 'rgb(247, 247, 247)',
  headFeaturesFont: {
    fontSize: 13,
    fontWeight: 'bold'
  },
  headTitleFont: {
    fontSize: 24,
    fontWeight: 'bold'
  },

  showTitle: true,
  titleAlignment: 'center',
  titleTextColor: 'rgb(255, 255, 255)',
  titleCaptionColor: 'rgba(255, 255, 255, 0.7)',
  titleFont: {
    fontSize: 24,
    fontWeight: 'bold',
    fontStyle: 'normal',
    textAlign: 'center'
  },
  titleCaptionFont: {
    fontSize: 15
  },

  showFeatures: true,
  featuresStyle: 'striped',
  featuresAlign: 'center',
  featuresFont: {
    fontSize: 13
  },
  featuresIconColor: 'rgb(25, 123, 255)',
  featuresTextColor: 'rgb(17, 17, 17)',

  showPrice: true,
  priceCaptionColor: 'rgba(17, 17, 17, 0.7)',
  discountTextColor: '#ff3838',
  discountFont: {
    fontSize: 11,
    fontStyle: 'normal',
    fontWeight: 'normal',
    textTransform: 'uppercase'
  },
  oldPriceColor: 'rgba(17, 17, 17, 0.5)',
  oldPriceFont: {
    fontSize: 13,
    fontWeight: 'normal',
    fontStyle: 'normal'
  },
  priceCaptionFont: {
    fontSize: 13,
    fontWeight: 'normal',
    fontStyle: 'normal'
  },
  priceAlign: 'center',
  priceColor: 'rgb(17, 17, 17)',
  priceFont: {
    fontSize: 24,
    fontWeight: 'bold',
    fontStyle: 'normal'
  },

  showPicture: true,
  pictureAspectRatio: 0.6666,

  showButton: true,
  button: widgetButtonDefaults,
  buttonAlignment: 'center',

  highlightStyleType: 'bar',
  highlightBackgroundColor: 'rgb(25, 123, 255)',
  highlightTextColor: 'rgb(255, 255, 255)',

  formBackgroundColor: 'rgb(255, 255, 255)',
  formTextColor: 'rgb(17, 17, 17)',
  formAccentColor: 'rgb(25, 123, 255)',
  formSuccessColor: 'rgb(14, 139, 37)',
  formErrorColor: 'rgb(255, 56, 56)',

  customCSS: '',
  customJS: ''
};
